// src/components/calendar/dialogs/ConfirmInactivateDialog.js
import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  Alert,
  Divider,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AssessmentIcon from "@mui/icons-material/Assessment";

const normalize = (s) => (s || "").trim().toLowerCase();

const ConfirmInactivateDialog = ({
  open,
  onClose,
  onConfirm,

  // confirm state
  confirmationText,
  setConfirmationText,
  errorText,

  // context
  stationName,
  positionName,
  periodLabel,

  // loading state
  loading = false,

  // ✅ NUEVO: modo éxito + acciones post-cierre
  success = false,
  onGoReports, // opcional: si se pasa, se muestra botón "Ir a reportes"
}) => {
  const isValid = useMemo(
    () => normalize(confirmationText) === "cerrar",
    [confirmationText]
  );

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: success ? "rgba(76,175,80,0.15)" : "rgba(255,152,0,0.15)",
            }}
          >
            {success ? (
              <AssessmentIcon sx={{ color: "success.main" }} />
            ) : (
              <LockOutlinedIcon sx={{ color: "warning.main" }} />
            )}
          </Box>

          <Box>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
              {success ? "Calendario cerrado" : "Cerrar calendario"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {success
                ? "La edición quedó bloqueada. Podés generar reportes de este período."
                : "Bloquea la edición de este período."}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            El calendario se cerró correctamente.
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Luego de cerrar, <b>no vas a poder editar</b> turnos, licencias ni días libres en este período.
            El calendario seguirá disponible para consulta.
          </Alert>
        )}

        {(stationName || positionName || periodLabel) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {success ? "Período cerrado:" : "Estás por cerrar:"}
            </Typography>

            {positionName && (
              <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                {positionName}
              </Typography>
            )}

            <Typography variant="body2" color="text.secondary">
              {stationName ? `Estación: ${stationName}` : ""}
              {stationName && periodLabel ? " · " : ""}
              {periodLabel ? `Período: ${periodLabel.replace(" - ", " a ")}` : ""}
            </Typography>

            <Divider sx={{ mt: 2 }} />
          </Box>
        )}

        {!success && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                ¿Qué cambia?
              </Typography>
              <Typography variant="body2" color="text.secondary" component="div">
                • No se podrá editar la planificación.<br />
                • El contenido se mantiene visible e imprimible.<br />
                • Se conserva como referencia para auditoría.
              </Typography>
            </Box>

            <TextField
              fullWidth
              label='Para confirmar, escribí "cerrar"'
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder="cerrar"
              autoFocus
              disabled={loading}
              error={Boolean(errorText) || (confirmationText.length > 0 && !isValid)}
              helperText={
                errorText ||
                (isValid ? "Confirmación correcta." : 'Escribí "cerrar" para habilitar el botón.')
              }
            />
          </>
        )}

        {success && (
          <Typography variant="body2" color="text.secondary">
            Si querés, podés ir ahora a <b>Reportes</b> para ver la actividad del período y exportar a Excel.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {success ? (
          <>
            <Button onClick={onClose} variant="text" disabled={loading}>
              Cerrar
            </Button>
            {onGoReports && (
              <Button onClick={onGoReports} variant="contained" disabled={loading}>
                Ir a reportes
              </Button>
            )}
          </>
        ) : (
          <>
            <Button onClick={onClose} variant="text" disabled={loading}>
              Cancelar
            </Button>
            <Button
              onClick={onConfirm}
              variant="contained"
              color="warning"
              disabled={!isValid || loading}
            >
              Cerrar calendario
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmInactivateDialog;
